import React, { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Wrench, FileImage, Images, FileText, Minimize2, ImageDown, Ruler,
  Combine, Split, RotateCw, FilePlus, Upload, X, Loader2, Download,
  ArrowLeft, GripVertical, ArrowUp, ArrowDown,
} from "lucide-react";

const fmtBytes = (n) => {
  if (n == null || isNaN(n)) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const TOOLS = [
  { key: "pdf-to-jpg",    title: "PDF → JPG",     desc: "Extract pages as high-quality JPG images",      icon: FileImage,  accent: "from-orange-500 to-red-500" },
  { key: "jpg-to-pdf",    title: "JPG/PNG → PDF", desc: "Merge multiple images into a single PDF",       icon: Images,     accent: "from-blue-500 to-indigo-500" },
  { key: "pdf-to-docx",   title: "PDF → Word",    desc: "Editable DOCX preserving layout when possible", icon: FileText,   accent: "from-sky-500 to-cyan-500" },
  { key: "pdf-compress",  title: "PDF Compress",  desc: "Shrink large PDFs — Low / Medium / High",       icon: Minimize2,  accent: "from-emerald-500 to-teal-500" },
  { key: "image-compress",title: "Image Compress",desc: "Reduce photo size · quality or target-size",    icon: ImageDown,  accent: "from-fuchsia-500 to-pink-500" },
  { key: "image-resize",  title: "Image Resize",  desc: "Exact px / cm / mm with aspect-ratio lock",     icon: Ruler,      accent: "from-violet-500 to-purple-500" },
  { key: "pdf-merge",     title: "PDF Merge",     desc: "Combine multiple PDFs — reorder before merge",  icon: Combine,    accent: "from-lime-500 to-green-500" },
  { key: "pdf-split",     title: "PDF Split",     desc: "Split by ranges — get a ZIP of PDF chunks",     icon: Split,      accent: "from-amber-500 to-yellow-500" },
  { key: "pdf-rotate",    title: "PDF Rotate",    desc: "Rotate 90° / 180° / 270° — all or per page",    icon: RotateCw,   accent: "from-rose-500 to-red-500" },
  { key: "extract-pages", title: "Extract Pages", desc: "Pick specific pages and save as one PDF",       icon: FilePlus,   accent: "from-slate-600 to-slate-800" },
];

// ─────────────────────────────────────────────────────────────
//  Generic file drop-zone
// ─────────────────────────────────────────────────────────────

function DropZone({ multiple, accept, onFiles, hint, testid = "dropzone" }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    const list = Array.from(e.dataTransfer.files || []);
    if (list.length) onFiles(list);
  };
  return (
    <label
      className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl cursor-pointer transition p-8 text-center ${over ? "bg-blue-50 border-blue-500" : "bg-slate-50 border-slate-300 hover:bg-slate-100"}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      data-testid={testid}
    >
      <Upload className="h-10 w-10 mb-2 text-slate-500" />
      <div className="text-sm font-medium">Tap to upload{multiple ? " files" : " a file"} · or drop here</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      <input
        ref={inputRef} type="file" accept={accept} multiple={!!multiple} className="hidden"
        onChange={(e) => { const l = Array.from(e.target.files || []); if (l.length) onFiles(l); e.target.value = ""; }}
      />
    </label>
  );
}

function FileRow({ file, meta, onRemove, extra, testid, onUp, onDown }) {
  return (
    <div className="flex items-center gap-3 border rounded-lg p-3 bg-white" data-testid={testid}>
      {(onUp || onDown) && (
        <div className="flex flex-col">
          <button className="hover:bg-slate-100 rounded p-0.5" onClick={onUp} data-testid={`${testid}-up`} aria-label="Move up"><ArrowUp className="h-3 w-3" /></button>
          <button className="hover:bg-slate-100 rounded p-0.5" onClick={onDown} data-testid={`${testid}-down`} aria-label="Move down"><ArrowDown className="h-3 w-3" /></button>
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{file.name}</div>
        <div className="text-xs text-muted-foreground truncate">{fmtBytes(file.size)}{meta ? ` · ${meta}` : ""}</div>
        {extra}
      </div>
      <button className="p-1 rounded hover:bg-red-50" onClick={onRemove} data-testid={`${testid}-remove`}>
        <X className="h-4 w-4 text-red-500" />
      </button>
    </div>
  );
}

async function downloadBlob(resp, fallbackName) {
  const dispo = resp.headers?.["content-disposition"] || resp.headers?.get?.("content-disposition") || "";
  const m = /filename="([^"]+)"/.exec(dispo);
  const name = m ? m[1] : fallbackName;
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
  return { name, size: resp.data.size, headers: resp.headers };
}

// ─────────────────────────────────────────────────────────────
//  Tool components (one per key)
// ─────────────────────────────────────────────────────────────

function ToolShell({ title, children }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold flex items-center gap-2">
        <Wrench className="h-5 w-5 text-blue-600" /> {title}
      </h2>
      {children}
    </div>
  );
}

function PdfToJpg() {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState("all");
  const [dpi, setDpi] = useState(200);
  const [quality, setQuality] = useState(90);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file) return toast.error("Pick a PDF");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("pages", pages); fd.append("dpi", dpi); fd.append("quality", quality);
      const resp = await api.post("/free-tools/pdf-to-jpg", fd, { responseType: "blob" });
      await downloadBlob(resp, "pdf_to_jpg.zip");
      toast.success("ZIP ready");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Convert failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="PDF → JPG">
      {!file
        ? <DropZone accept="application/pdf" onFiles={(l) => setFile(l[0])} hint="Any PDF up to 100 MB" testid="ptj-drop" />
        : <FileRow file={file} onRemove={() => setFile(null)} testid="ptj-file" />}
      <div className="grid gap-3 md:grid-cols-3">
        <div><Label>Pages</Label><Input value={pages} onChange={(e) => setPages(e.target.value)} placeholder="e.g. all or 1,3-5" data-testid="ptj-pages" /></div>
        <div><Label>Resolution (DPI)</Label><Input type="number" min={72} max={600} value={dpi} onChange={(e) => setDpi(+e.target.value || 200)} data-testid="ptj-dpi" /></div>
        <div><Label>Quality %</Label><Input type="number" min={30} max={100} value={quality} onChange={(e) => setQuality(+e.target.value || 90)} data-testid="ptj-quality" /></div>
      </div>
      <Button onClick={run} disabled={!file || busy} className="w-full md:w-auto" data-testid="ptj-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Convert & download ZIP
      </Button>
    </ToolShell>
  );
}

function JpgToPdf() {
  const [files, setFiles] = useState([]);
  const [paper, setPaper] = useState("a4");
  const [orientation, setOrientation] = useState("portrait");
  const [cw, setCw] = useState(210); const [ch, setCh] = useState(297);
  const [margin, setMargin] = useState(5);
  const [busy, setBusy] = useState(false);
  const move = (i, d) => setFiles((f) => { const j = i + d; if (j < 0 || j >= f.length) return f; const c = [...f]; [c[i], c[j]] = [c[j], c[i]]; return c; });
  const run = async () => {
    if (!files.length) return toast.error("Pick 1+ images");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("paper", paper); fd.append("orientation", orientation); fd.append("margin_mm", margin);
      if (paper === "custom") { fd.append("custom_w_mm", cw); fd.append("custom_h_mm", ch); }
      const resp = await api.post("/free-tools/jpg-to-pdf", fd, { responseType: "blob" });
      await downloadBlob(resp, "images.pdf");
      toast.success("PDF ready");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Convert failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="JPG / PNG → PDF">
      <DropZone accept="image/jpeg,image/png" multiple onFiles={(l) => setFiles((prev) => [...prev, ...l])} hint="Up to 50 images · reorder with arrows" testid="jtp-drop" />
      <div className="space-y-2">
        {files.map((f, i) => (
          <FileRow key={i} file={f} testid={`jtp-file-${i}`}
            onUp={() => move(i, -1)} onDown={() => move(i, 1)}
            onRemove={() => setFiles((fs) => fs.filter((_, j) => j !== i))} />
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <div><Label>Paper</Label>
          <Select value={paper} onValueChange={setPaper}>
            <SelectTrigger data-testid="jtp-paper"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="a4">A4</SelectItem>
              <SelectItem value="a5">A5</SelectItem>
              <SelectItem value="letter">Letter</SelectItem>
              <SelectItem value="legal">Legal</SelectItem>
              <SelectItem value="4x6">4×6 Photo</SelectItem>
              <SelectItem value="custom">Custom mm</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Orientation</Label>
          <Select value={orientation} onValueChange={setOrientation}>
            <SelectTrigger data-testid="jtp-orient"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="portrait">Portrait</SelectItem>
              <SelectItem value="landscape">Landscape</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Margin (mm)</Label><Input type="number" value={margin} min={0} max={50} onChange={(e) => setMargin(+e.target.value || 0)} data-testid="jtp-margin" /></div>
        {paper === "custom" && (
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" value={cw} min={30} max={500} onChange={(e) => setCw(+e.target.value || 210)} placeholder="W mm" data-testid="jtp-cw" />
            <Input type="number" value={ch} min={30} max={700} onChange={(e) => setCh(+e.target.value || 297)} placeholder="H mm" data-testid="jtp-ch" />
          </div>
        )}
      </div>
      <Button onClick={run} disabled={!files.length || busy} className="w-full md:w-auto" data-testid="jtp-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Convert & download PDF
      </Button>
    </ToolShell>
  );
}

function PdfToDocx() {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState("all");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file) return toast.error("Pick a PDF");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("pages", pages);
      const resp = await api.post("/free-tools/pdf-to-docx", fd, { responseType: "blob" });
      await downloadBlob(resp, "converted.docx");
      toast.success("DOCX ready");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Convert failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="PDF → Word (editable DOCX)">
      {!file
        ? <DropZone accept="application/pdf" onFiles={(l) => setFile(l[0])} hint="Layout is preserved as best as technically possible. Up to 50 MB." testid="ptd-drop" />
        : <FileRow file={file} onRemove={() => setFile(null)} testid="ptd-file" />}
      <div><Label>Pages</Label><Input value={pages} onChange={(e) => setPages(e.target.value)} placeholder="e.g. all or 1,3-5" data-testid="ptd-pages" /></div>
      <Button onClick={run} disabled={!file || busy} data-testid="ptd-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Convert & download DOCX
      </Button>
    </ToolShell>
  );
}

function PdfCompress() {
  const [file, setFile] = useState(null);
  const [level, setLevel] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {orig, final}
  const run = async () => {
    if (!file) return toast.error("Pick a PDF");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("level", level);
      const resp = await api.post("/free-tools/pdf-compress", fd, { responseType: "blob" });
      const orig = +resp.headers["x-original-size"] || file.size;
      const final = +resp.headers["x-final-size"] || resp.data.size;
      setResult({ orig, final });
      await downloadBlob(resp, "compressed.pdf");
      const saved = Math.round((1 - final / orig) * 100);
      if (saved > 0) toast.success(`Saved ${saved}%`);
      else toast.info("Already well-compressed — try a higher level for more savings");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Compress failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="PDF Compress">
      {!file
        ? <DropZone accept="application/pdf" onFiles={(l) => setFile(l[0])} hint="Scan-heavy PDFs benefit most" testid="pc-drop" />
        : <FileRow file={file} onRemove={() => { setFile(null); setResult(null); }} testid="pc-file" />}
      <div>
        <Label>Compression level</Label>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="md:w-72" data-testid="pc-level"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Low  · 200 DPI · best quality</SelectItem>
            <SelectItem value="medium">Medium · 150 DPI · recommended</SelectItem>
            <SelectItem value="high">High · 96 DPI · smallest size</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button onClick={run} disabled={!file || busy} data-testid="pc-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Compress & download
      </Button>
      {result && (
        <Card className="p-4 bg-slate-50" data-testid="pc-result">
          <div className="text-sm">
            Original <b>{fmtBytes(result.orig)}</b> → Final <b className="text-emerald-600">{fmtBytes(result.final)}</b>{" "}
            <Badge className="ml-2 bg-emerald-600 text-white">{Math.max(0, Math.round((1 - result.final / result.orig) * 100))}% smaller</Badge>
          </div>
        </Card>
      )}
    </ToolShell>
  );
}

function ImageCompress() {
  const [file, setFile] = useState(null);
  const [quality, setQuality] = useState(75);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!file) return toast.error("Pick an image");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("quality", quality);
      if (target) fd.append("target_kb", target);
      const resp = await api.post("/free-tools/image-compress", fd, { responseType: "blob" });
      const orig = +resp.headers["x-original-size"] || file.size;
      const final = +resp.headers["x-final-size"] || resp.data.size;
      setResult({ orig, final });
      await downloadBlob(resp, "compressed.jpg");
      toast.success("Compressed");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Compress failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="Image Compress">
      {!file
        ? <DropZone accept="image/*" onFiles={(l) => setFile(l[0])} hint="JPG or PNG · up to 30 MB" testid="ic-drop" />
        : <FileRow file={file} onRemove={() => { setFile(null); setResult(null); }} testid="ic-file" />}
      <div className="grid gap-3 md:grid-cols-2">
        <div><Label>Quality · {quality}%</Label><Input type="range" min={20} max={95} value={quality} onChange={(e) => setQuality(+e.target.value)} data-testid="ic-quality" /></div>
        <div><Label>Target size (KB, optional)</Label><Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 150" data-testid="ic-target" /></div>
      </div>
      <Button onClick={run} disabled={!file || busy} data-testid="ic-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Compress & download
      </Button>
      {result && (
        <Card className="p-4 bg-slate-50" data-testid="ic-result">
          Original <b>{fmtBytes(result.orig)}</b> → Final <b className="text-emerald-600">{fmtBytes(result.final)}</b>{" "}
          <Badge className="ml-2 bg-emerald-600 text-white">{Math.max(0, Math.round((1 - result.final / result.orig) * 100))}% smaller</Badge>
        </Card>
      )}
    </ToolShell>
  );
}

function ImageResize() {
  const [file, setFile] = useState(null);
  const [w, setW] = useState(1024); const [h, setH] = useState(768);
  const [unit, setUnit] = useState("px");
  const [dpi, setDpi] = useState(300);
  const [lock, setLock] = useState(true);
  const [fmt, setFmt] = useState("png");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file) return toast.error("Pick an image");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("width", w); fd.append("height", h);
      fd.append("unit", unit); fd.append("dpi", dpi);
      fd.append("lock_aspect", lock ? "true" : "false"); fd.append("fmt", fmt);
      const resp = await api.post("/free-tools/image-resize", fd, { responseType: "blob" });
      await downloadBlob(resp, `resized.${fmt}`);
      toast.success("Resized");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Resize failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="Image Resize">
      {!file
        ? <DropZone accept="image/*" onFiles={(l) => setFile(l[0])} hint="Any JPG or PNG" testid="ir-drop" />
        : <FileRow file={file} onRemove={() => setFile(null)} testid="ir-file" />}
      <div className="grid gap-3 md:grid-cols-3">
        <div><Label>Width</Label><Input type="number" min={1} value={w} onChange={(e) => setW(+e.target.value || 0)} data-testid="ir-w" /></div>
        <div><Label>Height</Label><Input type="number" min={1} value={h} onChange={(e) => setH(+e.target.value || 0)} data-testid="ir-h" /></div>
        <div><Label>Unit</Label>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger data-testid="ir-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="px">Pixels</SelectItem>
              <SelectItem value="cm">Centimetres</SelectItem>
              <SelectItem value="mm">Millimetres</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>DPI (for cm/mm)</Label><Input type="number" value={dpi} min={72} max={600} onChange={(e) => setDpi(+e.target.value || 300)} data-testid="ir-dpi" /></div>
        <div><Label>Output format</Label>
          <Select value={fmt} onValueChange={setFmt}>
            <SelectTrigger data-testid="ir-fmt"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="png">PNG</SelectItem>
              <SelectItem value="jpg">JPG</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Switch checked={lock} onCheckedChange={setLock} data-testid="ir-lock" />
          <Label className="text-sm">Lock aspect ratio</Label>
        </div>
      </div>
      <Button onClick={run} disabled={!file || busy} data-testid="ir-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Resize & download
      </Button>
    </ToolShell>
  );
}

function PdfMerge() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const move = (i, d) => setFiles((f) => { const j = i + d; if (j < 0 || j >= f.length) return f; const c = [...f]; [c[i], c[j]] = [c[j], c[i]]; return c; });
  const run = async () => {
    if (files.length < 2) return toast.error("Add at least 2 PDFs");
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const resp = await api.post("/free-tools/pdf-merge", fd, { responseType: "blob" });
      await downloadBlob(resp, "merged.pdf");
      toast.success("Merged");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Merge failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="PDF Merge">
      <DropZone accept="application/pdf" multiple onFiles={(l) => setFiles((p) => [...p, ...l])} hint="Add 2 or more PDFs · reorder with arrows" testid="pm-drop" />
      <div className="space-y-2">
        {files.map((f, i) => (
          <FileRow key={i} file={f} testid={`pm-file-${i}`}
            onUp={() => move(i, -1)} onDown={() => move(i, 1)}
            onRemove={() => setFiles((fs) => fs.filter((_, j) => j !== i))} />
        ))}
      </div>
      <Button onClick={run} disabled={files.length < 2 || busy} data-testid="pm-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Merge & download
      </Button>
    </ToolShell>
  );
}

function PdfSplit() {
  const [file, setFile] = useState(null);
  const [ranges, setRanges] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const readInfo = async (f) => {
    setFile(f); setInfo(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/free-tools/pdf-info", fd);
      setInfo(data);
    } catch (e) { /* ignore, just no info */ }
  };
  const run = async () => {
    if (!file) return toast.error("Pick a PDF");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("ranges", ranges);
      const resp = await api.post("/free-tools/pdf-split", fd, { responseType: "blob" });
      await downloadBlob(resp, "split.zip");
      toast.success("Split ready");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Split failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="PDF Split">
      {!file
        ? <DropZone accept="application/pdf" onFiles={(l) => readInfo(l[0])} hint="Split by ranges — leave blank to split every page" testid="ps-drop" />
        : <FileRow file={file} meta={info ? `${info.page_count} pages` : ""} onRemove={() => { setFile(null); setInfo(null); }} testid="ps-file" />}
      <div><Label>Ranges (leave blank = split every page)</Label><Input value={ranges} onChange={(e) => setRanges(e.target.value)} placeholder="e.g. 1-3, 4, 5-7" data-testid="ps-ranges" /></div>
      <Button onClick={run} disabled={!file || busy} data-testid="ps-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Split & download ZIP
      </Button>
    </ToolShell>
  );
}

function PdfRotate() {
  const [file, setFile] = useState(null);
  const [degrees, setDegrees] = useState(90);
  const [pages, setPages] = useState("all");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!file) return toast.error("Pick a PDF");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("degrees", degrees); fd.append("pages", pages);
      const resp = await api.post("/free-tools/pdf-rotate", fd, { responseType: "blob" });
      await downloadBlob(resp, "rotated.pdf");
      toast.success("Rotated");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Rotate failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="PDF Rotate">
      {!file
        ? <DropZone accept="application/pdf" onFiles={(l) => setFile(l[0])} hint="Rotate the whole PDF or specific pages" testid="pr-drop" />
        : <FileRow file={file} onRemove={() => setFile(null)} testid="pr-file" />}
      <div className="grid gap-3 md:grid-cols-2">
        <div><Label>Rotation</Label>
          <Select value={String(degrees)} onValueChange={(v) => setDegrees(+v)}>
            <SelectTrigger data-testid="pr-degrees"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="90">90° clockwise</SelectItem>
              <SelectItem value="180">180°</SelectItem>
              <SelectItem value="270">270° (90° CCW)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Pages</Label><Input value={pages} onChange={(e) => setPages(e.target.value)} placeholder="all or 1,3-5" data-testid="pr-pages" /></div>
      </div>
      <Button onClick={run} disabled={!file || busy} data-testid="pr-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Rotate & download
      </Button>
    </ToolShell>
  );
}

function ExtractPages() {
  const [file, setFile] = useState(null);
  const [pages, setPages] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const readInfo = async (f) => {
    setFile(f); setInfo(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/free-tools/pdf-info", fd);
      setInfo(data);
    } catch { /* ignore */ }
  };
  const run = async () => {
    if (!file) return toast.error("Pick a PDF");
    if (!pages.trim()) return toast.error("Enter pages to extract");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("pages", pages);
      const resp = await api.post("/free-tools/extract-pages", fd, { responseType: "blob" });
      await downloadBlob(resp, "extracted.pdf");
      toast.success("Extracted");
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Extract failed"); }
    finally { setBusy(false); }
  };
  return (
    <ToolShell title="Extract Pages">
      {!file
        ? <DropZone accept="application/pdf" onFiles={(l) => readInfo(l[0])} hint="Save selected pages as one PDF" testid="ep-drop" />
        : <FileRow file={file} meta={info ? `${info.page_count} pages` : ""} onRemove={() => { setFile(null); setInfo(null); }} testid="ep-file" />}
      <div><Label>Pages to extract</Label><Input value={pages} onChange={(e) => setPages(e.target.value)} placeholder="e.g. 1,3-5,8" data-testid="ep-pages" /></div>
      <Button onClick={run} disabled={!file || busy} data-testid="ep-run">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        Extract & download PDF
      </Button>
    </ToolShell>
  );
}

const RENDER = {
  "pdf-to-jpg": PdfToJpg,
  "jpg-to-pdf": JpgToPdf,
  "pdf-to-docx": PdfToDocx,
  "pdf-compress": PdfCompress,
  "image-compress": ImageCompress,
  "image-resize": ImageResize,
  "pdf-merge": PdfMerge,
  "pdf-split": PdfSplit,
  "pdf-rotate": PdfRotate,
  "extract-pages": ExtractPages,
};

// ─────────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────────

export default function FreeTools() {
  const [sp, setSp] = useSearchParams();
  const active = sp.get("tool");
  const Tool = active ? RENDER[active] : null;

  const open = (k) => setSp({ tool: k });
  const back = () => setSp({});

  return (
    <div className="space-y-6 pb-16" data-testid="free-tools-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Wrench className="h-7 w-7 text-blue-600" /> Free Document Tools
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Ten professional utilities for every MeeSeva / Xerox / CSC shop —
            completely free. Nothing is stored on our servers and no wallet
            balance is used.
          </p>
        </div>
        {active && (
          <Button variant="outline" onClick={back} data-testid="ft-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> All tools
          </Button>
        )}
      </div>

      {!active && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => open(t.key)}
                className="text-left rounded-2xl p-5 border border-slate-200 bg-white hover:border-blue-500 hover:shadow-lg transition group focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid={`ft-tile-${t.key}`}
              >
                <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${t.accent} text-white mb-3`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="font-semibold text-slate-900 group-hover:text-blue-700">{t.title}</div>
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.desc}</div>
                <div className="mt-3">
                  <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">100% Free</Badge>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {Tool && (
        <Card className="p-5" data-testid={`ft-panel-${active}`}>
          <Tool />
        </Card>
      )}
    </div>
  );
}
